// Libraries
import React, { useEffect, useState } from "react";
import { useObserver } from "mobx-react-lite";
import { runInAction } from "mobx";
import moment from "moment";

import MaterialTable from "material-table";

// Custom
import { DetailsPage } from "Globals/Views/DetailsPage/DetailsPage.styles";
import { PrimaryButton } from "Components/Buttons/Buttons";
import { IEItemsWrapper } from "Views/Project/Commercial/IETables/IEItemsWrapper";
import { IEHireTrackerReportViewModel } from "./IEHireTrackerReportViewModel";
import { Loader } from "@shoothill/core";
import { RunReportButtonContainer } from "Views/Report/Report.styles";
// (Header removed as you’re mirroring the columns inside the table)
import { DarwinTablePageContent } from "Globals/Styles/AppStyling";
import { PatchedPagination } from "Components/Table/PatchedPagination";
import * as Defaults from "Globals/Defaults/TableOptions";
import { HireTrackerItemViewModel } from "./HireTrackerItemViewModel";
import { DarwinSelect } from "Components/AutoComplete/DarwinSelect";
import { DarwinDateSelect } from "Components/DateSelect/DarwinDateSelect";
import { DarwinInputWrapper } from "Components/Form/DarwinInputWrapper";
import { HireTrackerItemModel } from "./HireTrackerItemModel";
import { SnackBar } from "Components/SnackBar/SnackBar";
import { Typography } from "@material-ui/core";

// Locale
moment.locale("en-GB", { week: { dow: 1 } });

interface Props {
    ieId: string | null;
    projectId: string | null;
}

export const IEHireTrackerReportView: React.FunctionComponent<Props> = (props: Props) => {
    const [viewModel] = useState(() => new IEHireTrackerReportViewModel());
    const [forceRerender, setForceRerender] = useState(false);

    useEffect(() => {
        return () => viewModel.reset();
    }, []);

    const runReport = () => {
        viewModel.runReport(props.ieId, props.projectId);
        viewModel.setCanExportCSV(true);
    };

    const exportCSV = () => {
        viewModel.generateIEHireTrackerReportCSV(props.ieId, props.projectId);
    };

    const handleUpsert = async (row: HireTrackerItemViewModel) => {
        if (!(await row.isModelValid())) {
            setForceRerender((prev) => !prev);
            return;
        }
        viewModel.handleSetIsLoading(true);
        row.upsertItemHireInformation()
            .then((apiResult) => {
                if (apiResult.wasSuccessful) {
                    runInAction(() => {
                        viewModel.setSnackMessage("Saved hire item");
                        viewModel.setSnackType(viewModel.SNACKSUCCESS);
                        viewModel.setSnackbarState(true);
                    });
                } else {
                    runInAction(() => {
                        viewModel.setSnackMessage("Failed to save hire item");
                        viewModel.setSnackType(viewModel.SNACKERROR);
                        viewModel.setSnackbarState(true);
                    });
                }
            })
            .finally(() => {
                viewModel.handleSetIsLoading(false);
            });
        setForceRerender((prev) => !prev);
    };

    // // Helper for input[type=date] value formatting (YYYY-MM-DD)
    // const toInputDate = (d: Date | null | undefined) => (d ? moment(d).format("YYYY-MM-DD") : "");

    return useObserver(() => (
        <DetailsPage>
            <RunReportButtonContainer>
                <PrimaryButton displayName={"Run report"} execute={runReport} fullWidth={false} canExecute={!viewModel.IsLoading} />
                {viewModel.reportViewModel && (
                    <PrimaryButton displayName={"Export CSV"} execute={exportCSV} fullWidth={false} canExecute={!viewModel.IsLoading && viewModel.canExportCSV} />
                )}
            </RunReportButtonContainer>

            <br />

            <Typography variant={"h5"}>Please review and complete the manual sections before running this report.</Typography>

            <IEItemsWrapper>
                <div className="content" style={{ paddingTop: "30px", position: "relative", minWidth: "1600px" }}>
                    {!viewModel.IsLoading && viewModel.reportViewModel && (
                        <DarwinTablePageContent>
                            <MaterialTable<HireTrackerItemViewModel>
                                components={{ Pagination: PatchedPagination }}
                                options={Defaults.GetDarwinTableOptionsNoSearchV2()}
                                isLoading={viewModel.IsLoading}
                                title=""
                                columns={[
                                    // Supplier
                                    { title: "Supplier", field: "supplierName", render: (row) => row.supplierName },

                                    // Hire status (DarwinSelect; options come from page VM; value stored on item VM)
                                    {
                                        title: "Hire status",
                                        field: "hireStatusName",
                                        render: (row) => (
                                            <DarwinSelect
                                                displayName=""
                                                stopPropagation={true}
                                                fullWidth={true}
                                                options={viewModel.hireStatuses} // <-- from page VM
                                                getOptionLabel={(option: any) => option.displayName}
                                                placeholder="Please select"
                                                // value expects the *option object*; compute from row.hireStatusId
                                                value={viewModel.handleReturnStatus(row)} // helper on page VM that returns {id, displayName} | null
                                                // write the id/name back to the *item* VM
                                                executeWithEvent={(e, data: { id: string; displayName: string }) => {
                                                    row.setHireStatus(data.id, data.displayName);
                                                }}
                                                onBlur={() => row.isFieldValid("hireStatusId" as any)}
                                                error={row.getError("hireStatusId") !== ""}
                                                validationMessage={row.getError("hireStatusId")}
                                                // Toggle this to your real permission flag if you have one
                                                canExecute={!viewModel.IsLoading && !row.IsLoading}
                                            />
                                        ),
                                    },

                                    // Item
                                    { title: "Item", field: "description", render: (row) => row.description },

                                    // PO Number
                                    { title: "PO Number", field: "formattedPONumber", render: (row) => row.formattedPONumber },

                                    // PO start date
                                    {
                                        title: "PO start date",
                                        field: "hireStartDate",
                                        render: (row) => (row.hireStartDate ? moment(row.hireStartDate).format("DD/MM/YYYY") : ""),
                                        defaultSort: "asc",
                                    },

                                    // PO end date
                                    {
                                        title: "PO end date",
                                        field: "hireEndDate",
                                        render: (row) => (row.hireEndDate ? moment(row.hireEndDate).format("DD/MM/YYYY") : ""),
                                    },

                                    // Off Hired date (editable)
                                    {
                                        title: "Off Hired date",
                                        field: "offHireDate",
                                        render: (row) => (
                                            <DarwinDateSelect
                                                displayName=""
                                                execute={(value: string | null) => row.setValue("offHireDate", value)}
                                                onBlur={() => row.isFieldValid("offHireDate")}
                                                placeholder="Please select"
                                                validationMessage={row.getError("offHireDate")}
                                                value={row.getValue("offHireDate")}
                                                canExecute={!viewModel.IsLoading && !row.IsLoading}
                                            />
                                        ),
                                    },

                                    // Price
                                    { title: "Price", field: "unitPrice", type: "numeric", render: (row) => row.unitPriceFormatted },

                                    // Quantity
                                    { title: "Quantity", field: "quantity", type: "numeric", render: (row) => row.quantity, width: "5%" },

                                    // Hire total
                                    { title: "Hire total", field: "total", type: "numeric", render: (row) => row.totalFormatted },

                                    // Notes (editable)
                                    {
                                        title: "Notes",
                                        field: "hireNote",
                                        render: (row) => (
                                            <DarwinInputWrapper<HireTrackerItemModel>
                                                type="text"
                                                label=""
                                                placeHolder="Hire note"
                                                validateOnBlur={true}
                                                viewModel={row}
                                                fieldName="hireNote"
                                                shrink={true}
                                                maxLength={2000}
                                                multiLine={true}
                                                rows={5}
                                                editMode={!viewModel.IsLoading && !row.IsLoading}
                                            />
                                        ),
                                        width: "15%",
                                    },

                                    // Duration
                                    { title: "Duration", field: "duration", render: (row) => row.durationFormatted, align: "right" },

                                    // Save action
                                    {
                                        title: "Save",
                                        field: "actions",
                                        sorting: false,
                                        render: (row) => (
                                            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                                <PrimaryButton
                                                    displayName="Save"
                                                    fullWidth={false}
                                                    canExecute={!viewModel.IsLoading && !row.IsLoading}
                                                    execute={() => {
                                                        handleUpsert(row);
                                                    }}
                                                />
                                            </div>
                                        ),
                                    },
                                ]}
                                data={viewModel.items}
                                onRowClick={() => {
                                    /* no-op */
                                }}
                            />
                        </DarwinTablePageContent>
                    )}

                    {viewModel.IsLoading && <Loader />}
                </div>
            </IEItemsWrapper>
            <SnackBar
                messageText={viewModel.snackMessage}
                messageType={viewModel.snackType}
                active={viewModel.snackbarState}
                closeOption={() => viewModel.setSnackbarState(false)}
                autoHideDuration={5000}
            />
        </DetailsPage>
    ));
};
